var _a;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import '../styles/dropdown.scss';
import globalStore from '../store';
// Reliable touch-primary detection — works on modern iPads (desktop UA)
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
import ButtonComponent from './ButtonComponent';
import CustomScrollbar from './CustomScrollbar';
import { useOnResize } from '../helper/hooks/onResize';
import { t } from 'i18next';
import { getImageSrc } from '../helper/imageBlobCache';
var Dropdown = function (_a) {
    var _b;
    var _c, _d;
    var _e = _a.id, id = _e === void 0 ? 'dropDown' : _e, options = _a.options, value = _a.value, onChange = _a.onChange, _f = _a.align, align = _f === void 0 ? 'right' : _f, _g = _a.optionWidthType, optionWidthType = _g === void 0 ? 'tillHalfBtn' : _g, _h = _a.placeHolder, placeHolder = _h === void 0 ? t('text.select') : _h, _j = _a.className, className = _j === void 0 ? '' : _j, labelId = _a.labelId, _k = _a.disabled, disabled = _k === void 0 ? false : _k, _l = _a.ddHeight, ddHeight = _l === void 0 ? 130 : _l, _m = _a.controlHeightFromOutside, controlHeightFromOutside = _m === void 0 ? false : _m, _o = _a.selectedDisable, selectedDisable = _o === void 0 ? false : _o, tabIndex = _a.tabIndex;
    var _p = useState(false), open = _p[0], setOpen = _p[1];
    var _q = useState(-1), focusedIndex = _q[0], setFocusedIndex = _q[1];
    var _r = useState('bottom'), popupPos = _r[0], setPopupPos = _r[1];
    var _s = useState('normal'), btnState = _s[0], setBtnState = _s[1];
    var menuId = "".concat(id, "-menu");
    var getOptionId = function (idx) { return "".concat(id, "-option-").concat(idx); };
    var btnConfig = globalStore.useStoreState(function (store) { return store.jsonDataStore; }).btnConfig;
    var btnRef = useRef(null);
    var justSelectedRef = useRef(false);
    var btnPart1 = useRef(null);
    var btnPart2 = useRef(null);
    var containerRef = useRef(null);
    var listRef = useRef(null);
    var optionRefs = useRef([]);
    // Close dropdown on outside click
    useEffect(function () {
        var handler = function (e) {
            var _a;
            if (listRef.current &&
                !listRef.current.contains(e.target) &&
                !((_a = btnRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return function () { return document.removeEventListener('mousedown', handler); };
    }, []);
    var updateListWidth = useCallback(function () {
        if (open && btnRef.current && listRef.current) {
            var rect = btnRef.current.getBoundingClientRect();
            switch (optionWidthType) {
                case 'tillOption':
                    if (btnPart1.current) {
                        listRef.current.style.width = "".concat(btnPart1.current.getBoundingClientRect().width, "px");
                    }
                    break;
                case 'full':
                    listRef.current.style.width = "".concat(rect.width, "px");
                    break;
                case 'tillHalfBtn':
                    if (btnPart1.current && btnPart2.current) {
                        var _width = btnPart1.current.getBoundingClientRect().width +
                            btnPart2.current.getBoundingClientRect().width / 2;
                        listRef.current.style.width = "".concat(_width, "px");
                    }
                    break;
            }
        }
    }, [open]);
    // Match popup width + auto-position
    useLayoutEffect(function () {
        if (open && btnRef.current && listRef.current) {
            updateListWidth();
        }
    }, [open, optionWidthType]);
    useOnResize(updateListWidth);
    // Focus the active menuitem when dropdown opens or focusedIndex changes
    // On touch devices (VoiceOver), only scroll into view — don't force-focus,
    // as it competes with VoiceOver's own cursor management.
    useEffect(function () {
        if (open && focusedIndex >= 0) {
            var el = optionRefs.current[focusedIndex];
            if (!IS_TOUCH_DEVICE) {
                el === null || el === void 0 ? void 0 : el.focus();
            }
            el === null || el === void 0 ? void 0 : el.scrollIntoView({ block: 'nearest' });
        }
    }, [open, focusedIndex]);
    // Close dropdown when focus leaves the container.
    // On touch devices (VoiceOver), skip blur-based closing — VoiceOver manages
    // focus independently and document.activeElement doesn't match VoiceOver's cursor,
    // so the check fires prematurely and closes the dropdown. The outside-click
    // handler (mousedown listener) still works as a safety net.
    var handleContainerBlur = useCallback(function (e) {
        var _a;
        if (IS_TOUCH_DEVICE)
            return;
        var relatedTarget = e.relatedTarget;
        if (relatedTarget && ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(relatedTarget))) {
            return; // focus stayed within dropdown
        }
        setTimeout(function () {
            if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
                setOpen(false);
            }
        }, 150);
    }, []);
    // Keyboard navigation
    var handleKeyDown = function (e) {
        var _a, _b;
        if (disabled)
            return;
        if (!open) {
            // After a fresh selection, the next ArrowDown/ArrowUp should NOT reopen the dropdown —
            // it should move focus past it (standard JAWS navigation expectation).
            // preventDefault still stops the browser from scrolling the page.
            if (justSelectedRef.current && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                justSelectedRef.current = false;
                e.preventDefault();
                return;
            }
            // Standard ARIA combobox pattern: Enter/Space/ArrowDown/ArrowUp open the dropdown.
            if (e.key === 'Enter' ||
                e.key === ' ' ||
                e.key === 'ArrowDown' ||
                e.key === 'ArrowUp') {
                e.preventDefault();
                setOpen(true);
                setFocusedIndex(value ? options.indexOf(value) : 0);
            }
            return;
        }
        switch (e.key) {
            case 'Escape':
                setOpen(false);
                (_a = btnRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                e.preventDefault();
                e.stopPropagation();
                break;
            case 'ArrowDown':
                setFocusedIndex(function (prev) { return Math.min(prev + 1, options.length - 1); });
                e.preventDefault();
                break;
            case 'ArrowUp':
                setFocusedIndex(function (prev) { return Math.max(prev - 1, 0); });
                e.preventDefault();
                break;
            case 'Home':
                setFocusedIndex(0);
                e.preventDefault();
                break;
            case 'End':
                setFocusedIndex(options.length - 1);
                e.preventDefault();
                break;
            case 'Enter':
            case ' ': {
                var selected = options[focusedIndex];
                if (selected) {
                    if (!(selectedDisable && value === selected)) {
                        onChange(id, { value: selected, index: focusedIndex });
                    }
                    setOpen(false);
                    justSelectedRef.current = true;
                    // Defer focus restoration so it survives React re-render from onChange.
                    setTimeout(function () { var _a; return (_a = btnRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, IS_TOUCH_DEVICE ? 100 : 0);
                    e.preventDefault();
                }
                break;
            }
            case 'Tab':
                setOpen(false);
                if (e.shiftKey) {
                    (_b = btnRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                }
                break;
        }
    };
    return (_jsxs("div", { ref: containerRef, onBlur: handleContainerBlur, className: "dropdownContainer ".concat(className, " ").concat(disabled && "".concat(className, "_disable")), children: [_jsx(ButtonComponent, { reference: btnRef, defaultCSS: "dropdownTrigger", onKeyDown: handleKeyDown, clickHandler: function () { return setOpen(function (p) { return !p; }); }, onMouseOver: function () { return setBtnState('hover'); }, onMouseOut: function () { return setBtnState('normal'); }, ariaHaspopup: true, ariaExpanded: open, ariaControls: open ? menuId : undefined, ariaLabel: labelId
                    ? "".concat((_d = (_c = document.getElementById(labelId)) === null || _c === void 0 ? void 0 : _c.textContent) !== null && _d !== void 0 ? _d : '', ", ").concat(value ? "".concat(value, " ").concat(t('text.selected')) : placeHolder)
                    : value ? "".concat(value, ", ").concat(t('text.selected')) : placeHolder, disabled: disabled, tabIndex: tabIndex, children: _jsxs("div", { className: "dropDownBtnGrid", children: [_jsx("div", { className: "dropDownSelected align-".concat(align, " ").concat(value === null ? (open ? 'placeHolderNormal' : 'placeHolderOpen') : ''), ref: btnPart1, children: value || placeHolder }), _jsx("div", { ref: btnPart2, className: "dropDownArrow ".concat(btnState === 'hover' ? 'dropdownTriggerImg_hover' : ''), children: _jsx("img", { src: getImageSrc(btnConfig.arrowBtn.imgSrc[btnState]), alt: "", "aria-hidden": "true" }) })] }) }), open && (_jsx("div", { ref: listRef, className: "dropdownList position-".concat(popupPos), style: (_b = {},
                    _b['--popup-max-height'] = controlHeightFromOutside ? "".concat(ddHeight + 20, "px") : '160px',
                    _b), children: _jsx(CustomScrollbar, { direction: "vertical", className: "verticalList", style: { height: controlHeightFromOutside ? ddHeight : 130 }, tabIndex: -1, children: _jsx("div", { id: menuId, role: "menu", "aria-labelledby": labelId, className: "dropdownListInner", children: options.map(function (item, idx) { return (_jsx("div", { ref: function (el) {
                                optionRefs.current[idx] = el;
                            }, id: getOptionId(idx), role: "menuitem", "aria-label": "".concat(item, ", ").concat(idx + 1, " ").concat(t('text.of'), " ").concat(options.length), tabIndex: focusedIndex === idx ? 0 : -1, "aria-current": value === item ? true : undefined, className: "dropdownOption ".concat(focusedIndex === idx ? 'focused' : '', " ").concat(value === item ? 'selected' : '', " ").concat(selectedDisable && value === item ? 'optionDisable' : '', " align-").concat(align), onKeyDown: handleKeyDown, onFocus: function () { return setFocusedIndex(idx); }, onMouseOver: function () { return setFocusedIndex(idx); }, onClick: function () {
                                if (selectedDisable && value === item)
                                    return;
                                onChange(id, { value: item, index: idx });
                                setOpen(false);
                                justSelectedRef.current = true;
                                // Defer focus so it survives the re-render from onChange
                                setTimeout(function () { var _a; return (_a = btnRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, IS_TOUCH_DEVICE ? 100 : 0);
                            }, children: item }, item)); }) }) }) }))] }));
};
export default Dropdown;
